import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DateTime } from "luxon";
import { useEffect } from "react";

const STORAGE_KEY = "somethings-support-chat-mute";

/**
 * Epoch millis for the next 12:00 AM Eastern. Muting the support chat lasts
 * until this moment, at which point notifications resume automatically.
 */
export function nextMidnightEtMillis(): number {
  return DateTime.now()
    .setZone("America/New_York")
    .plus({ days: 1 })
    .startOf("day")
    .toMillis();
}

interface SupportChatMuteStore {
  /** Epoch millis when the mute expires, or null when not muted. */
  mutedUntil: number | null;
  /** User the mute belongs to, so a different account isn't affected. */
  userId: string | null;
  mute: (userId: string | null) => void;
  unmute: () => void;
}

export const useSupportChatMuteStore = create<SupportChatMuteStore>()(
  persist(
    (set) => ({
      mutedUntil: null,
      userId: null,
      mute: (userId) => set({ mutedUntil: nextMidnightEtMillis(), userId }),
      unmute: () => set({ mutedUntil: null, userId: null }),
    }),
    { name: STORAGE_KEY },
  ),
);

const isActiveForUser = (
  mutedUntil: number | null,
  mutedUserId: string | null,
  userId?: string | null,
): boolean => {
  if (mutedUntil == null) return false;
  if (Date.now() >= mutedUntil) return false;
  if (userId != null && mutedUserId != null && userId !== mutedUserId) {
    return false;
  }
  return true;
};

/**
 * Non-reactive check for use outside React (e.g. Sendbird message handlers).
 * Lazily clears the mute once it has expired.
 */
export function isSupportChatMuted(userId?: string | null): boolean {
  const state = useSupportChatMuteStore.getState();
  if (state.mutedUntil != null && Date.now() >= state.mutedUntil) {
    state.unmute();
    return false;
  }
  return isActiveForUser(state.mutedUntil, state.userId, userId);
}

/**
 * Reactive hook for the mute toggle UI. Auto-flips back to unmuted when the
 * expiry (midnight ET) passes while the component is mounted.
 */
export function useSupportChatMuted(userId?: string | null): boolean {
  const mutedUntil = useSupportChatMuteStore((s) => s.mutedUntil);
  const mutedUserId = useSupportChatMuteStore((s) => s.userId);
  const unmute = useSupportChatMuteStore((s) => s.unmute);

  useEffect(() => {
    if (mutedUntil == null) return;
    const remaining = mutedUntil - Date.now();
    if (remaining <= 0) {
      unmute();
      return;
    }
    const timeout = setTimeout(unmute, remaining);
    return () => clearTimeout(timeout);
  }, [mutedUntil, unmute]);

  return isActiveForUser(mutedUntil, mutedUserId, userId);
}

// Keep tabs in sync: when another tab mutes/unmutes, rehydrate this store so
// the toggle UI and notification gate reflect the change immediately.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) {
      void useSupportChatMuteStore.persist.rehydrate();
    }
  });
}
